'use client'

import { useState, useTransition } from 'react'
import { useRouter, useParams } from 'next/navigation'
import Link from 'next/link'
import Image from 'next/image'
import { useLanguage } from '@/lib/LanguageContext'
import { sendOtp, verifyOtp } from '@/actions/user'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSeparator,
    InputOTPSlot,
} from "@/components/ui/input-otp"
import { Loader2, ArrowLeft, ArrowRight, Mail } from 'lucide-react'
import { ModeToggle } from '@/components/mode-toggle'
import { LanguageSwitcher } from '@/components/dashboard/LanguageSwitcher'
import { StripedPattern } from '@/components/ui/striped-pattern'

export function ForgotPasswordPage() {
    const [step, setStep] = useState<'email' | 'otp'>('email')
    const [email, setEmail] = useState('')
    const [otp, setOtp] = useState('')
    const [isPending, startTransition] = useTransition()
    const [error, setError] = useState<string | null>(null)
    const [resendTimer, setResendTimer] = useState(0)

    const router = useRouter()
    const params = useParams()
    const locale = (params.locale as string) || 'ar'
    const { dir } = useLanguage()
    const isRTL = dir === 'rtl'

    // Simplified Translations
    const t = {
        backToLogin: locale === 'ar' ? 'العودة لتسجيل الدخول' : 'Back to Login',
        title: locale === 'ar' ? 'نسيت كلمة المرور؟' : 'Forgot Password?',
        subtitle: locale === 'ar' ? 'أدخل بريدك الإلكتروني لإرسال رمز التحقق.' : 'Enter your email to receive a verification code.',
        email: locale === 'ar' ? 'البريد الإلكتروني' : 'Email Address',
        otpLabel: locale === 'ar' ? 'رمز التحقق (OTP)' : 'Verification Code',
        sendCode: locale === 'ar' ? 'إرسال الرمز' : 'Send Code',
        verifyCode: locale === 'ar' ? 'تحقق ومتابعة' : 'Verify & Continue',
        sending: locale === 'ar' ? 'جاري الإرسال...' : 'Sending...',
        verifying: locale === 'ar' ? 'جاري التحقق...' : 'Verifying...',
        enterOtpTitle: locale === 'ar' ? 'أدخل رمز التحقق' : 'Enter Verification Code',
        enterOtpDesc: locale === 'ar' ? `قمنا بإرسال رمز مكون من 6 أرقام إلى ${email}` : `We sent a 6-digit code to ${email}`,
        resend: locale === 'ar' ? 'إعادة الإرسال' : 'Resend Code',
        resendIn: (s: number) => locale === 'ar' ? `إعادة الإرسال خلال ${s}ث` : `Resend in ${s}s`,
    }

    const handleSendOtp = (e: React.FormEvent) => {
        e.preventDefault()
        setError(null)

        startTransition(async () => {
            const result = await sendOtp(email)

            if (result.error) {
                setError(result.error)
            } else {
                setStep('otp')
                startResendTimer()
            }
        })
    }

    const handleVerifyOtp = (e: React.FormEvent) => {
        e.preventDefault()
        setError(null)

        if (otp.length !== 6) {
            setError(locale === 'ar' ? 'الرمز يجب أن يتكون من 6 أرقام' : 'Code must be 6 digits')
            return
        }

        startTransition(async () => {
            const result = await verifyOtp(email, otp)
            if (result.error) {
                setError(result.error)
            } else {
                router.push(`/${locale}/reset-password`)
            }
        })
    }

    const startResendTimer = () => {
        setResendTimer(60)
        const interval = setInterval(() => {
            setResendTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(interval)
                    return 0
                }
                return prev - 1
            })
        }, 1000)
    }

    const handleResend = () => {
        if (resendTimer > 0) return
        handleSendOtp({ preventDefault: () => { } } as React.FormEvent)
    }

    return (
        <div className="h-screen flex items-center justify-center bg-background relative overflow-hidden" dir={dir}>
            {/* Global Background Pattern - Lowest Z-Index */}
            <StripedPattern
                className="text-foreground/[0.03] dark:text-foreground/[0.02] z-0"
            />

            {/* Controls - Fixed to top start corner */}
            <div className="absolute top-4 left-4 rtl:left-auto rtl:right-4 z-50 flex items-center gap-2">
                <LanguageSwitcher locale={locale} />
                <ModeToggle />
            </div>

            <div className="w-full h-full grid lg:grid-cols-2 p-4 relative z-10">
                {/* Form Side */}
                <div className="flex items-center justify-center py-12 px-8 relative">
                    <div className="mx-auto grid w-full max-w-[400px] gap-6">
                        {/* Header */}
                        <div className="flex flex-col space-y-2 text-center items-center">
                            <Link href="/" className="mb-4 flex flex-col items-center gap-2 group">
                                <Image
                                    src="/logo-light.png"
                                    alt="Ecommaps Logo"
                                    width={40}
                                    height={40}
                                    className="size-10 object-contain block dark:hidden"
                                    unoptimized
                                />
                                <Image
                                    src="/logo-dark.png"
                                    alt="Ecommaps Logo"
                                    width={40}
                                    height={40}
                                    className="size-10 object-contain hidden dark:block"
                                    unoptimized
                                />
                            </Link>
                            <h1 className="text-3xl font-bold tracking-tight text-primary">
                                {step === 'email' ? t.title : t.enterOtpTitle}
                            </h1>
                            <p className="text-sm text-balance text-muted-foreground">
                                {step === 'email' ? t.subtitle : t.enterOtpDesc}
                            </p>
                        </div>

                        <div className="grid gap-6">
                            {error && (
                                <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md text-center">
                                    {error}
                                </div>
                            )}

                            {step === 'email' ? (
                                <form onSubmit={handleSendOtp}>
                                    <div className="grid gap-4">
                                        <div className="grid gap-2">
                                            <Label htmlFor="email">{t.email}</Label>
                                            <div className="relative">
                                                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground rtl:right-3 rtl:left-auto" />
                                                <Input
                                                    id="email"
                                                    placeholder="name@example.com"
                                                    type="email"
                                                    className="pl-9 rtl:pr-9 rtl:pl-3 bg-background/80 backdrop-blur-sm"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    disabled={isPending}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <Button
                                            type="submit"
                                            className="w-full mt-2 from-primary to-primary/85 text-primary-foreground dark:inset-shadow-2xs dark:inset-shadow-white/10 bg-linear-to-t border border-b-2 border-zinc-950/40 shadow-md shadow-zinc-950/20 ring-1 ring-inset ring-white/25 transition-[filter] duration-200 hover:brightness-110 active:brightness-90 dark:border-x-0 dark:border-t-0 dark:border-zinc-950/50 dark:ring-white/5 rounded-md"
                                            disabled={isPending}
                                        >
                                            {isPending ? (
                                                <>
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                    {t.sending}
                                                </>
                                            ) : (
                                                t.sendCode
                                            )}
                                        </Button>
                                    </div>
                                </form>
                            ) : (
                                <form onSubmit={handleVerifyOtp}>
                                    <div className="grid gap-4">
                                        <div className="flex flex-col items-center gap-4 my-2" dir="ltr">
                                            <InputOTP
                                                maxLength={6}
                                                value={otp}
                                                onChange={(val: string) => setOtp(val)}
                                                disabled={isPending}
                                            >
                                                <InputOTPGroup>
                                                    <InputOTPSlot index={0} />
                                                    <InputOTPSlot index={1} />
                                                    <InputOTPSlot index={2} />
                                                </InputOTPGroup>
                                                <InputOTPSeparator />
                                                <InputOTPGroup>
                                                    <InputOTPSlot index={3} />
                                                    <InputOTPSlot index={4} />
                                                    <InputOTPSlot index={5} />
                                                </InputOTPGroup>
                                            </InputOTP>
                                        </div>

                                        <Button
                                            type="submit"
                                            className="w-full mt-2 from-primary to-primary/85 text-primary-foreground dark:inset-shadow-2xs dark:inset-shadow-white/10 bg-linear-to-t border border-b-2 border-zinc-950/40 shadow-md shadow-zinc-950/20 ring-1 ring-inset ring-white/25 transition-[filter] duration-200 hover:brightness-110 active:brightness-90 dark:border-x-0 dark:border-t-0 dark:border-zinc-950/50 dark:ring-white/5 rounded-md"
                                            disabled={isPending || otp.length !== 6}
                                        >
                                            {isPending ? (
                                                <>
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                    {t.verifying}
                                                </>
                                            ) : (
                                                t.verifyCode
                                            )}
                                        </Button>

                                        <div className="text-center">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                type="button"
                                                onClick={handleResend}
                                                disabled={resendTimer > 0 || isPending}
                                                className="text-xs text-muted-foreground"
                                            >
                                                {resendTimer > 0 ? t.resendIn(resendTimer) : t.resend}
                                            </Button>
                                        </div>
                                    </div>
                                </form>
                            )}

                            <div className="text-center">
                                <Link href={`/${locale}/login`}>
                                    <Button variant="link" className="gap-2 text-muted-foreground hover:text-primary">
                                        {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
                                        {t.backToLogin}
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Image Side - Matches Login Page */}
                <div className="hidden bg-muted lg:block rounded-lg border relative overflow-hidden bg-slate-900 shadow-sm">
                    <div className="absolute inset-0 bg-[url('https://ecodata.ecommaps.com/storage/v1/object/public/store-assets/login-background-final.png')] bg-cover bg-center"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent"></div>
                    <div className="relative z-20 flex h-full flex-col justify-between p-12 text-white pointer-events-none">
                        <div className="flex items-center gap-2 text-2xl font-bold">
                            <Image
                                src="/logo-dark.png"
                                alt="Ecommaps Logo"
                                width={32}
                                height={32}
                                className="w-8 h-8 object-contain"
                                unoptimized
                            />
                            Ecommaps
                        </div>
                        <div className="space-y-4">
                            <blockquote className="space-y-2">
                                <p className="text-lg">
                                    &ldquo;Secure your business with world-class authentication. Recovery is just a step away.&rdquo;
                                </p>
                            </blockquote>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
